import { useCallback, useEffect, useState } from "react";
import { TriggeredHook } from "sonobello.utilities.react";

import { DisqualifyingReason, DisqualifyingReasonsType } from "../../../../dtos/DisqualifyingReason";
import { LeadResult } from "../../../../dtos/LeadResult";
import { useObx } from "../../../../utils/UseObx";
import ApiRequestPaths from "../../../Constants/ApiRequestPaths";
import MedicalNonCanLeadResult from "../Types/MedicalNonCanLeadResult";

/** A hook which dispatches a network request to create a disqualifying lead result for medical non-candidacy.
 * @remarks This hook returns a usable value even if the network request fails.
 */
const useResilientCreateMedicalNonCanLeadResult: TriggeredHook<
  LeadResult,
  undefined,
  boolean,
  { leadId: string; patientId: string; patientOpportunityId: string; disqualifyingReasons: DisqualifyingReason[] }
> = ({ leadId, patientId, patientOpportunityId, disqualifyingReasons }) => {
  const [returnValue, setReturnValue] = useState<LeadResult>();

  const { res, err, loading, setReq } = useObx<LeadResult, LeadResult>("Post LeadResult", {
    method: "post",
    url: ApiRequestPaths.postLeadResult(leadId)
  });

  const execute = useCallback(() => {
    const reason = DisqualifyingReason.GetReason(disqualifyingReasons, DisqualifyingReasonsType.ObxMedicalNoncandidate);
    setReq(r => ({
      ...r,
      payload: new MedicalNonCanLeadResult(patientId, patientOpportunityId, reason.id)
    }));
  }, [setReq]);

  useEffect(() => {
    if (!res && !err) return;
    const reason = DisqualifyingReason.GetReason(disqualifyingReasons, DisqualifyingReasonsType.ObxMedicalNoncandidate);
    setReturnValue(res?.payload || new MedicalNonCanLeadResult(patientId, patientOpportunityId, reason.id));
  }, [res, err]);

  return { execute, isLoading: loading, result: returnValue || null, error: Boolean(err) };
};

export default useResilientCreateMedicalNonCanLeadResult;
